import { ViolationInstallmentDialog } from "./ViolationInstallmentDialog";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { isActiveEmp } from "../lib/constants";
import { Money } from "./SaudiRiyal";
import { toast } from "sonner";
import { AlertTriangle, Plus, Trash2, Camera, Loader2, ExternalLink, Upload, X, Copy, Sparkles, Image as ImageIcon, Download, Search } from "lucide-react";
import html2canvas from "html2canvas";
import { Button } from "./ui/button";
import { BulkTools } from "./BulkTools";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api, fileUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useBulkSelect, RowCheck, BulkBar } from "./common/BulkSelect";

const KPI = ({ label, value, tone, onClick, active, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-2xl border bg-white p-4 text-start transition hover:shadow-md ${active ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"}`}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`font-heading text-xl font-bold tabular-nums ${tone || "text-slate-800"}`}>{value}</p>
  </button>
);
const SEL = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";

const CreateDialog = ({ open, onOpenChange, employees, onSaved }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState({ employee_id: "", type: "", location: "", amount: "", points: "", violation_date: "", violation_number: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm({ employee_id: "", type: "", location: "", amount: "", points: "", violation_date: "", violation_number: "", description: "" }); setPhoto(null); } }, [open]);

  const save = async () => {
    if (!form.employee_id || !form.type.trim()) { toast.warning(t("dash.viol.warnPick")); return; }
    setBusy(true);
    try {
      let photo_id = null, photo_name = null;
      if (photo) { const up = await api.uploadFile(photo); photo_id = up.id || up.file_id || up.attachment_id; photo_name = photo.name; }
      await api.createViolation({ ...form, amount: Number(form.amount || 0), points: Number(form.points || 0), photo_id, photo_name });
      toast.success(t("dash.viol.saved")); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.viol.saveFail")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="violation-create-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{t("dash.viol.createTitle")}</DialogTitle>
          <DialogDescription className="text-right">{t("dash.viol.createDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>{t("dash.viol.rider")}</Label>
            <select data-testid="violation-employee-select" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">{t("dash.viol.pickRider")}</option>
              {employees.filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{`${e.name} — ${e.branch || "—"}`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("dash.viol.type")}</Label><Input data-testid="violation-type-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder={t("dash.viol.typePh")} /></div>
            <div className="space-y-1.5"><Label>رقم المخالفة</Label><Input data-testid="violation-number-input" dir="ltr" value={form.violation_number} onChange={(e) => setForm({ ...form, violation_number: e.target.value })} placeholder="اختياري" /></div>
            <div className="space-y-1.5"><Label>{t("dash.viol.date")}</Label><Input data-testid="violation-date-input" type="date" value={form.violation_date} onChange={(e) => setForm({ ...form, violation_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("dash.viol.amount")}</Label><Input data-testid="violation-amount-input" type="number" dir="ltr" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("dash.viol.points")}</Label><Input type="number" dir="ltr" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("dash.viol.location")}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={t("dash.viol.locationPh")} /></div>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> {t("dash.viol.photo")}</Label>
            <Input data-testid="violation-photo-input" type="file" accept="image/*,.pdf" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            {photo && <span className="text-xs text-emerald-600">{photo.name}</span>}
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="violation-save-btn" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("dash.common.save")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.common.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Muqeem-style export card (inline styles so html2canvas renders it reliably)
const ExportCard = ({ innerRef, rider, iqama, rows }) => {
  const money = (n) => <Money value={n} />;
  const total = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
  const cell = { padding: "10px 12px", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #eef2f7" };
  const th = { padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#64748b", background: "#f8fafc", textAlign: "center" };
  return (
    <div ref={innerRef} dir="rtl" style={{ width: 680, background: "#ffffff", fontFamily: "'Tajawal','Segoe UI',Arial,sans-serif", color: "#0f172a", border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
      <div style={{ background: "#065f46", color: "#fff", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>تقرير المخالفات المرورية</div>
        <div style={{ fontSize: 12, opacity: 0.9 }}>{new Date().toLocaleDateString("ar-SA")}</div>
      </div>
      <div style={{ padding: "16px 20px", display: "flex", gap: 24, flexWrap: "wrap", borderBottom: "1px solid #e2e8f0" }}>
        <div>
          <div style={{ fontSize: 11, color: "#64748b" }}>اسم المندوب</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{rider?.name || "—"}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#64748b" }}>الفرع</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{rider?.branch || "—"}</div>
        </div>
        <div style={{ marginInlineStart: "auto" }}>
          <div style={{ fontSize: 11, color: "#64748b" }}>رقم الإقامة</div>
          <div style={{ fontSize: 15, fontWeight: 800, direction: "ltr", textAlign: "right" }}>{iqama || "—"}</div>
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={th}>رقم المخالفة</th>
          <th style={th}>تاريخ المخالفة</th>
          <th style={th}>وصف المخالفة</th>
          <th style={th}>التكلفة</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td style={{ ...cell, textAlign: "center", color: "#94a3b8" }} colSpan={4}>لا توجد مخالفات</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...cell, textAlign: "center", direction: "ltr", fontWeight: 600 }}>{r.violation_number || "—"}</td>
              <td style={{ ...cell, textAlign: "center", fontSize: 12 }}>
                <div style={{ direction: "ltr" }}>{r.violation_date_hijri ? `${r.violation_date_hijri} هـ` : "—"}</div>
                <div style={{ color: "#94a3b8", direction: "ltr" }}>{r.violation_date ? `${r.violation_date} م` : ""}</div>
              </td>
              <td style={{ ...cell, textAlign: "right" }}>{r.type || "—"}</td>
              <td style={{ ...cell, textAlign: "center", fontWeight: 700, direction: "ltr" }}>{money(r.amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr>
          <td style={{ ...cell, background: "#f0fdf4", fontWeight: 800 }} colSpan={3}>الإجمالي</td>
          <td style={{ ...cell, background: "#f0fdf4", fontWeight: 800, textAlign: "center", color: "#065f46", direction: "ltr" }}>{money(total)}</td>
        </tr></tfoot>
      </table>
    </div>
  );
};

const ExportDialog = ({ open, onOpenChange, items, employees }) => {
  const { dir } = useLang();
  const cardRef = useRef(null);
  const [riderId, setRiderId] = useState("");
  const [busy, setBusy] = useState(false);

  const ridersWithViol = useMemo(() => {
    const seen = new Map();
    items.forEach((v) => { if (!seen.has(v.employee_id)) seen.set(v.employee_id, { id: v.employee_id, name: v.employee_name, branch: v.branch }); });
    return Array.from(seen.values());
  }, [items]);

  useEffect(() => { if (open) setRiderId(ridersWithViol[0]?.id || ""); }, [open, ridersWithViol]);

  const rider = employees.find((e) => e.id === riderId) || ridersWithViol.find((r) => r.id === riderId);
  const iqama = employees.find((e) => e.id === riderId)?.iqama_number || "";
  const rows = items.filter((v) => v.employee_id === riderId);

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `مخالفات-${rider?.name || "المندوب"}.png`;
      a.click();
      toast.success("تم تصدير الصورة");
    } catch (e) { toast.error("تعذّر إنشاء الصورة"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="violation-export-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">تصدير مخالفات المندوب كصورة</DialogTitle>
          <DialogDescription className="text-right">اختر المندوب لعرض بطاقة مخالفاته ثم حمّلها كصورة.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>المندوب</Label>
            <select data-testid="violation-export-rider" value={riderId} onChange={(e) => setRiderId(e.target.value)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              {ridersWithViol.length === 0 && <option value="">لا يوجد مناديب لديهم مخالفات</option>}
              {ridersWithViol.map((r) => <option key={r.id} value={r.id}>{`${r.name} — ${r.branch || "—"}`}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto rounded-xl bg-slate-50 p-3">
            <ExportCard innerRef={cardRef} rider={rider} iqama={iqama} rows={rows} />
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="violation-export-download" disabled={busy || !riderId} onClick={download} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تحميل الصورة
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ViolationsPanel = ({ canEdit, employees = [] }) => {
  const { t, dir } = useLang();
  const money = (n) => <Money value={n} />;
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [instV, setInstV] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fBranch, setFBranch] = useState("");
  const [fType, setFType] = useState("");
  const [fMonth, setFMonth] = useState("");
  const opts = useMemo(() => {
    const u = (k) => [...new Set(data.items.map(k).filter(Boolean))].sort();
    return { branches: u((v) => v.branch), types: u((v) => v.type), months: u((v) => String(v.violation_date || "").slice(0, 7)).reverse() };
  }, [data.items]);

  const filteredItems = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data.items.filter((v) =>
      (fStatus === "all" || (fStatus === "unpaid" ? !["paid", "deducted_unpaid"].includes(v.status) : v.status === fStatus))
      && (!fBranch || v.branch === fBranch) && (!fType || v.type === fType)
      && (!fMonth || String(v.violation_date || "").startsWith(fMonth))
      && (!s || [v.employee_name, v.branch, v.type, v.location, v.violation_number, v.description]
        .some((f) => String(f || "").toLowerCase().includes(s))));
  }, [data.items, q, fStatus, fBranch, fType, fMonth]);
  const fTotal = filteredItems.reduce((a, v) => a + Number(v.amount || 0), 0);
  const anyF = q || fStatus !== "all" || fBranch || fType || fMonth;
  const clearF = () => { setQ(""); setFStatus("all"); setFBranch(""); setFType(""); setFMonth(""); };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.importViolations(fd);
      const un = r.unmatched || [];
      toast.success(`تم استيراد ${r.inserted} مخالفة جديدة${r.updated ? ` · حُدّث ${r.updated}` : ""}${r.skipped ? ` · تُخطّي ${r.skipped} مكرر` : ""}`);
      setImportResult({ inserted: r.inserted || 0, total: r.total_rows || 0, unmatched: un });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر استيراد الملف");
    } finally { setImporting(false); }
  };

  const onExtractImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.extractViolationsImage(fd);
      if (!r.matched) {
        toast.warning(`رقم الإقامة ${r.iqama_number || ""} غير مرتبط بأي مندوب. تأكد من تسجيله في بيانات المناديب.`);
      } else if (r.inserted === 0) {
        toast.info(`المندوب ${r.employee_name}: لا مخالفات جديدة (${r.skipped || 0} مسجّلة مسبقًا).`);
      } else {
        toast.success(`تم ربط ${r.inserted} مخالفة بالمندوب ${r.employee_name}${r.skipped ? ` (تجاهل ${r.skipped} مكررة)` : ""}.`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر قراءة الصورة");
    } finally { setExtracting(false); }
  };

  const copyUnmatched = () => {
    const un = importResult?.unmatched || [];
    const text = un.map((u) => `${u.plate}\t${u.count}`).join("\n");
    navigator.clipboard?.writeText(text).then(
      () => toast.success("تم نسخ قائمة اللوحات غير المطابقة"),
      () => toast.error("تعذّر النسخ")
    );
  };

  const load = useCallback(async () => {
    try { setData(await api.listViolations()); } catch { toast.error(t("dash.viol.loadFail")); }
    finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (v, status) => {
    try { await api.updateViolation(v.id, { ...v, status }); load(); }
    catch { toast.error(t("dash.viol.updateFail")); }
  };
  const del = async (id) => { try { await api.deleteViolation(id); toast.success(t("dash.viol.deleted")); load(); } catch { toast.error(t("dash.viol.deleteFail")); } };
  const bulk = useBulkSelect();

  const statusPill = (v) => {
    const st = v.status === "paid" ? { c: "bg-emerald-100 text-emerald-700", l: t("dash.viol.paid") }
      : v.status === "deducted_unpaid" ? { c: "bg-amber-100 text-amber-700", l: t("dash.viol.deducted") }
      : { c: "bg-rose-100 text-rose-700", l: t("dash.viol.unpaid") };
    return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${st.c}`}>{st.l}</span>;
  };

  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="violations-panel" dir={dir}>
      <div className="grid grid-cols-3 gap-3">
        <KPI testid="viol-kpi-count" onClick={clearF} active={!anyF} label={t("dash.viol.kpiCount")} value={s.count || 0} />
        <KPI testid="viol-kpi-total" onClick={() => setFStatus("all")} label={t("dash.viol.kpiTotal")} value={money(s.total_amount)} tone="text-slate-800" />
        <KPI testid="viol-kpi-unpaid" onClick={() => setFStatus("unpaid")} active={fStatus === "unpaid"} label={t("dash.viol.kpiUnpaid")} value={money(s.unpaid)} tone="text-rose-600" />
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading text-lg font-bold text-slate-900">{t("dash.viol.title")}</h2>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" data-testid="violation-export-open" disabled={data.items.length === 0}
              onClick={() => setExportOpen(true)} className="gap-1.5 border-slate-200 text-slate-700 hover:bg-slate-50">
              <ImageIcon className="h-4 w-4" /> تصدير كصورة
            </Button>
            <input type="file" accept="image/*,.pdf" id="viol-extract-input" className="hidden"
              data-testid="violation-extract-input" onChange={onExtractImage} />
            <Button variant="outline" data-testid="violation-extract-btn" disabled={extracting}
              onClick={() => document.getElementById("viol-extract-input").click()}
              className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} استخراج من صورة مقيم
            </Button>
            <input type="file" accept=".xlsx,.xls" id="viol-import-input" className="hidden"
              data-testid="violation-import-input" onChange={onImportFile} />
            <Button variant="outline" data-testid="violation-import-btn" disabled={importing}
              onClick={() => document.getElementById("viol-import-input").click()}
              className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} استيراد Excel
            </Button>
            <BulkTools section="violations" onDone={load} />
            <Button data-testid="violation-create-open" onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              <Plus className="h-4 w-4" /> {t("dash.viol.createBtn")}
            </Button>
          </div>
        )}
      </div>

      {!loading && data.items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="violation-filters">
          <div className="relative w-full max-w-xs">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="violation-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث بالاسم أو الفرع أو الوصف أو رقم المخالفة" className="pr-9" />
          </div>
          <select data-testid="violation-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={SEL}>
            <option value="all">كل الحالات</option><option value="unpaid">{t("dash.viol.unpaid")}</option>
            <option value="deducted_unpaid">{t("dash.viol.deducted")}</option><option value="paid">{t("dash.viol.paid")}</option>
          </select>
          <select data-testid="violation-filter-branch" value={fBranch} onChange={(e) => setFBranch(e.target.value)} className={SEL}>
            <option value="">كل الفروع</option>{opts.branches.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="violation-filter-type" value={fType} onChange={(e) => setFType(e.target.value)} className={`${SEL} max-w-[200px]`}>
            <option value="">كل الأنواع</option>{opts.types.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="violation-filter-month" value={fMonth} onChange={(e) => setFMonth(e.target.value)} className={SEL}>
            <option value="">كل الأشهر</option>{opts.months.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          {anyF && <button type="button" onClick={clearF} data-testid="violation-filter-clear" className="text-xs font-semibold text-blue-600 hover:underline">مسح الفلاتر</button>}
          <span className="text-xs text-slate-500" data-testid="violation-filter-result">{filteredItems.length} مخالفة · {money(fTotal)}</span>
        </div>
      )}

      {importResult && importResult.unmatched.length > 0 && (
        <div data-testid="viol-unmatched-panel" className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div>
                <p className="font-heading text-sm font-bold text-amber-800">
                  {importResult.unmatched.length} لوحة لم تُطابق أي مندوب
                </p>
                <p className="mt-0.5 text-xs text-amber-700">
                  تم ربط {importResult.inserted} مخالفة من أصل {importResult.total} صف. اللوحات التالية لم يُعثر لها على مندوب مطابق — تأكد أن رقم لوحة المركبة مسجّل في بيانات المندوب.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" data-testid="viol-unmatched-copy" onClick={copyUnmatched} className="h-8 gap-1 px-2 text-amber-700 hover:bg-amber-100">
                <Copy className="h-3.5 w-3.5" /> نسخ
              </Button>
              <button data-testid="viol-unmatched-close" onClick={() => setImportResult(null)} className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {importResult.unmatched.map((u, i) => (
              <span key={i} data-testid={`viol-unmatched-item-${i}`} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800">
                <span dir="rtl">{u.plate}</span>
                {u.count > 1 && <span className="rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-600">×{u.count}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">
          <AlertTriangle className="mx-auto h-10 w-10 text-slate-300" /><p className="mt-2">{t("dash.viol.empty")}</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400" data-testid="violation-no-results">
          <Search className="mx-auto h-10 w-10 text-slate-300" /><p className="mt-2">لا توجد نتائج مطابقة لبحثك</p>
        </div>
      ) : (
        <div className="space-y-2">
        {canEdit && <BulkBar bulk={bulk} allIds={filteredItems.map((v) => v.id)} noun="مخالفة" prefix="violations-bulk"
          onDelete={async (ids) => { const r = await api.violationsBulkDelete(ids); load(); return r; }} />}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {canEdit && <th className="w-10 px-2 py-2.5" />}
              <th className="px-3 py-2.5 text-right">{t("dash.viol.rider")}</th><th className="px-3 py-2.5">{t("dash.viol.colType")}</th>
              <th className="px-3 py-2.5">{t("dash.viol.date")}</th><th className="px-3 py-2.5">{t("dash.viol.amount")}</th>
              <th className="px-3 py-2.5">{t("dash.viol.colPhoto")}</th><th className="px-3 py-2.5">{t("dash.viol.colStatus")}</th><th className="px-3 py-2.5">{t("dash.viol.colActions")}</th></tr></thead>
            <tbody>
              {filteredItems.map((v) => (
                <tr key={v.id} data-testid={`violation-row-${v.id}`} className={`border-t border-slate-100 ${bulk.sel.has(v.id) ? "bg-emerald-50/60" : ""}`}>
                  {canEdit && <td className="px-2 py-2.5"><RowCheck checked={bulk.sel.has(v.id)} onChange={() => bulk.toggle(v.id)} testid={`violation-check-${v.id}`} /></td>}
                  <td className="px-3 py-2.5 text-right"><div className="font-semibold text-slate-800">{v.employee_name}</div><div className="text-xs text-slate-400">{v.branch || "—"}{v.location ? ` · ${v.location}` : ""}</div></td>
                  <td className="px-3 py-2.5 text-center">
                    <div>{v.type}{v.points ? <span className="text-xs text-amber-600"> · {v.points} {t("dash.viol.pointsSuffix")}</span> : ""}</div>
                    {v.violation_number ? <div className="text-[11px] text-slate-400 font-mono-nums" dir="ltr">#{v.violation_number}</div> : null}
                  </td>
                  <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600">
                    <div dir="ltr">{v.violation_date_hijri ? `${v.violation_date_hijri} هـ` : (v.violation_date || "—")}</div>
                    {v.violation_date_hijri && v.violation_date ? <div className="text-[11px] text-slate-400" dir="ltr">{v.violation_date} م</div> : null}
                  </td>
                  <td className="px-3 py-2.5 text-center font-mono-nums font-semibold">{money(v.amount)}</td>
                  <td className="px-3 py-2.5 text-center">
                    {v.photo_id ? <a href={fileUrl(v.photo_id)} target="_blank" rel="noreferrer" data-testid={`violation-photo-${v.id}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"><ExternalLink className="h-3.5 w-3.5" /> {t("dash.viol.photoView")}</a> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">{statusPill(v)}</td>
                  <td className="px-3 py-2.5">
                    {canEdit && (
                      <div className="flex items-center justify-center gap-1.5">
                        <button data-testid={`violation-deducted-${v.id}`} onClick={() => setStatus(v, v.status === "deducted_unpaid" ? "unpaid" : "deducted_unpaid")}
                          className={`rounded-lg px-2 py-1 text-xs font-semibold border transition-colors ${v.status === "deducted_unpaid" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}>
                          {t("dash.viol.deducted")}
                        </button>
                        <button data-testid={`violation-paid-${v.id}`} onClick={() => setStatus(v, v.status === "paid" ? "unpaid" : "paid")}
                          className={`rounded-lg px-2 py-1 text-xs font-semibold border transition-colors ${v.status === "paid" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"}`}>
                          {v.status === "paid" ? t("dash.viol.paid") : "تعليم مسدّدة"}
                        </button>
                        <button data-testid={`violation-installment-${v.id}`} onClick={() => setInstV(v)}
                          className={`rounded-lg px-2 py-1 text-xs font-semibold border transition-colors ${v.installment_months > 0 ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}>
                          {v.installment_months > 0 ? `مقسّطة ${v.installment_months} ش` : "تقسيط"}
                        </button>
                        <button data-testid={`violation-delete-${v.id}`} onClick={() => del(v.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} employees={employees} onSaved={load} />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} items={data.items} employees={employees} />
      <ViolationInstallmentDialog v={instV} onOpenChange={(o) => !o && setInstV(null)} onSaved={load} />
    </div>
  );
};
